import { Link } from "wouter";
import { useState } from "react";
import { useT } from "../../i18n/index.js";
import { formatMoney } from "../../lib/format-fr.js";
import { formatFrenchPlate } from "../../lib/plate-format.js";
import { trpc } from "../../lib/trpc.js";

/**
 * F-operation-board (CEO 2026-09-20) — onde está cada carro da operação:
 * a reparar → em reparação → reparado → entregue.
 *
 * Mesma leitura em dois lugares: no painel do escritório (todos os clientes,
 * de chantier ou não) e na agenda. O gerente vê o equivalente dentro do portal
 * do chantier, limitado ao canteiro dele. O status vem inteiro do servidor
 * (`operation-status.ts`), então as três telas nunca discordam.
 */

export type OperationStatus = "a_reparar" | "em_reparacao" | "reparado" | "entregue";

/**
 * Tom do status no PORTAL (tela escura, fora do ErpShell) — Tailwind cru.
 * No ERP não dá para usar isto: lá o tema claro é o padrão e estas cores
 * nascem para fundo escuro, então saem lavadas. Ver OPERATION_BADGE.
 */
export const OPERATION_TONE: Record<OperationStatus, string> = {
  a_reparar: "bg-sky-500/15 text-sky-300",
  em_reparacao: "bg-amber-500/15 text-amber-300",
  reparado: "bg-violet-500/15 text-violet-300",
  entregue: "bg-lime/15 text-lime",
};

/** Tom do status no ERP: classes semânticas, funcionam nos dois temas. */
const OPERATION_BADGE: Record<OperationStatus, string> = {
  a_reparar: "badge b-info",
  em_reparacao: "badge b-warn",
  reparado: "badge b-violet",
  entregue: "badge b-ok",
};

export function useOperationLabels(): Record<OperationStatus, string> {
  const t = useT();
  return {
    a_reparar: t("operationBoard.statusToRepair"),
    em_reparacao: t("operationBoard.statusInRepair"),
    reparado: t("operationBoard.statusRepaired"),
    entregue: t("operationBoard.statusDelivered"),
  };
}

const ORDEM: OperationStatus[] = ["a_reparar", "em_reparacao", "reparado", "entregue"];

export function OperationBoard({
  companyId,
  /** Painel: poucas linhas e sem filtros. Agenda: quadro inteiro. */
  compact = false,
}: {
  companyId: number | null;
  compact?: boolean;
}) {
  const t = useT();
  const labels = useOperationLabels();
  const [chantierSel, setChantierSel] = useState<string>("");
  const [statusSel, setStatusSel] = useState<OperationStatus | null>(null);
  const [verEntregues, setVerEntregues] = useState(!compact);

  const chantiersQ = trpc.chantiers.list.useQuery(
    { companyId: companyId ?? 0, activeOnly: true },
    { enabled: companyId != null && !compact },
  );
  const boardQ = trpc.dossiers.operationBoard.useQuery(
    {
      companyId: companyId ?? 0,
      ...(chantierSel === ""
        ? {}
        : { chantierId: chantierSel === "sem" ? "sem" : Number(chantierSel) }),
      includeEntregues: true,
    },
    { enabled: companyId != null },
  );

  const counts = boardQ.data?.counts;
  const todos = boardQ.data?.cars ?? [];
  const visiveis = todos
    .filter((c) => (verEntregues ? true : c.status !== "entregue"))
    .filter((c) => (statusSel ? c.status === statusSel : true));
  const mostrados = compact ? visiveis.slice(0, 8) : visiveis;
  const restantes = visiveis.length - mostrados.length;

  return (
    <section className="panel" style={{ padding: 14, marginBottom: 14 }}>
      <div className="panel__h" style={{ marginBottom: 8 }}>
        <h2>
          🚗 {t("operationBoard.title")}
          {todos.length > 0 && (
            <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.6 }}>
              {" "}
              · {todos.length} {t("operationBoard.totalWord")}
            </span>
          )}
        </h2>
        {!compact && (
          <select
            value={chantierSel}
            onChange={(e) => setChantierSel(e.target.value)}
            className="rounded-md border border-neutral-800 bg-neutral-900 outline-none focus:border-lime"
            style={{ maxWidth: 260, fontSize: 12, padding: "4px 8px" }}
          >
            <option value="">{t("operationBoard.allCustomers")}</option>
            <option value="sem">{t("operationBoard.noChantier")}</option>
            {(chantiersQ.data?.rows ?? []).map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        {ORDEM.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusSel((cur) => (cur === s ? null : s))}
            className={`chip${statusSel === s ? " chip--active" : ""}`}
            style={{ gap: 8 }}
          >
            <span style={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              {counts ? counts[s] : "—"}
            </span>
            <span className={OPERATION_BADGE[s]}>
              <span className="led" />
              {labels[s]}
            </span>
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {/* CEO 2026-10-12: o chip só filtrava o quadro e "nada abria" — com um
            estado escolhido, link para a lista de dossiers nesse estado. */}
        {statusSel && (
          <Link
            href={`/dossiers?op=${statusSel}`}
            className="chip"
            data-testid="operation-open-list"
            style={{ textDecoration: "none" }}
          >
            {t("operationBoard.openList")} ({visiveis.length}) »
          </Link>
        )}
        <button type="button" onClick={() => setVerEntregues((v) => !v)} className="chip">
          {verEntregues ? t("operationBoard.hideDelivered") : t("operationBoard.showDelivered")}
        </button>
      </div>

      {mostrados.length === 0 ? (
        <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {statusSel ? t("operationBoard.empty") : t("operationBoard.emptyAll")}
        </p>
      ) : (
        <div>
          {mostrados.map((c) => (
            <Link
              key={c.id}
              href={`/dossiers/${c.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 4px",
                borderTop: "1px solid var(--border)",
                fontSize: 13,
                color: "var(--text-body)",
              }}
            >
              <span className={OPERATION_BADGE[c.status as OperationStatus]}>
                <span className="led" />
                {labels[c.status as OperationStatus]}
              </span>
              <span style={{ fontFamily: "monospace", fontSize: 12, color: "var(--text-strong)" }}>
                {c.plate ? formatFrenchPlate(c.plate) : (c.vin ?? c.dossierCode)}
              </span>
              <span className="truncate">{[c.make, c.model].filter(Boolean).join(" ")}</span>
              <span className="truncate" style={{ color: "var(--text-muted)" }}>
                · {c.customerName}
              </span>
              {c.chantierName && (
                <span className="hidden truncate sm:inline" style={{ color: "var(--text-muted)" }}>
                  · 🏗 {c.chantierName}
                </span>
              )}
              {!c.received && (
                <span className="badge b-mut">{t("operationBoard.notReceived")}</span>
              )}
              {c.franchise && (
                <span className="badge b-warn">{t("operationBoard.franchiseChip")}</span>
              )}
              {/* CEO 2026-10-13 — reparado com o dégarnissage sem registro. */}
              {c.degarnissagePending && c.status === "reparado" && (
                <span className="badge b-warn" title={t("operationBoard.degarnChipTitle")}>
                  ⚠ {t("operationBoard.degarnChip")}
                </span>
              )}
              <span style={{ flex: 1 }} />
              {c.technicians.length > 0 && (
                <span
                  className="hidden truncate md:inline"
                  style={{ fontSize: 11, color: "var(--text-muted)" }}
                >
                  {c.technicians.join(", ")}
                </span>
              )}
              {c.devisHt != null && (
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: 12,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatMoney(c.devisHt, { decimals: 2 })}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
      {restantes > 0 && (
        <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
          <Link
            href={statusSel ? `/dossiers?op=${statusSel}` : "/dossiers"}
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            + {restantes} {t("operationBoard.moreCars")} »
          </Link>
        </p>
      )}
    </section>
  );
}
